import React from 'react';
import useEscapeToClose from 'Components/Banking/useEscapeToClose';
import cancelBrokerageSession from 'Components/Banking/cancelBrokerageSession';
import applyBrokerageSession from 'Components/Banking/applyBrokerageSession';
import './Brokerage.css';

// Eingebettete (nicht als Overlay) Variante des PIN/TAN-Logins aus dem
// fruehreren BrokerageModal - wird von allen Brokerage-Seiten (Depotuebersicht,
// Orderbuch, Order aus einer Vorlage) gleich benutzt, weil jede von ihnen vor
// dem ersten comdirect-Zugriff denselben Login-Vorgang braucht. PIN/TAN leben
// nur lokal in diesem Formular, der fertige Token bleibt serverseitig in
// $_SESSION (siehe BrokerageTask.inc) - dieses Formular erfaehrt nur "fertig".
//
// PIN-Speicherung (Chat vom 2026-07-26, zweiter Anlauf): analog zu
// BankSyncModal.js wird proaktiv mit leerem PIN versucht (statt nur passiv
// auf eine leere Eingabe zu warten, wie beim ersten, nicht funktionierenden
// Anlauf). Schlaegt das fehl, faellt die UI automatisch auf manuelle Eingabe
// zurueck.
//
// Der Versuch haengt bewusst NICHT an props.pinStored: useBrokerageConnections
// laedt die Verbindungsliste (und damit dieses Flag) nur einmal beim
// Seitenaufruf. Wird der PIN erst danach (im selben Seitenaufruf) erstmals
// gemerkt, bliebe pinStored fuer den Rest der Session veraltet auf false -
// jeder weitere Login-Dialog wuerde dann faelschlich wieder nach der PIN
// fragen, obwohl sie serverseitig laengst gespeichert ist (genau dieser Bug
// wurde am 2026-07-26 gemeldet: "Nun wird die PIN schon wieder abgefragt").
// Der zusaetzliche Versuch mit leerem PIN ist auch ohne gespeicherten PIN
// harmlos (ein schneller Request, Antwort 'pin-required' faellt still auf
// die manuelle Eingabe zurueck).
// PushTAN-Polling wieder entfernt (Chat vom 2026-07-26): zwei Anlaeufe
// (wiederholter PATCH, dann ein rein lesender GET-Status-Check) haben beide
// die comdirect-App aus dem Tritt gebracht - erst ein App-Absturz, danach
// eine Endlosschleife neuer Push-Anfragen. Offenbar loest bei comdirect JEDER
// authentifizierte Aufruf mit dem noch nicht aktivierten Zwischen-Token
// (auch ein reiner GET) serverseitig eine neue Challenge aus, solange die
// Session noch nicht aktiv ist - es gibt also keinen sicheren Weg, das ohne
// Nutzerinteraktion zu pollen. Zurueck zum manuellen "Bestaetigen"-Klick.
export default function BrokerageLoginForm(props) {

	const x = props.x;
	const task = (name, data, cb) => x.was.axPost('Backend/webapp.php?module=Banking&task=' + name, data, cb);

	// Nicht direkt props.onCancel - vorher noch eine ggf. schon offene
	// comdirect-Verbindung widerrufen (vorlaeufiger Token waehrend TAN-
	// Eingabe), siehe cancelBrokerageSession.js.
	function handleCancel() {
		cancelBrokerageSession(x, props.connectionId, props.onCancel);
	}

	useEscapeToClose(props.onCancel ? handleCancel : undefined);

	const [pin, setPin] = React.useState('');
	const [remember, setRemember] = React.useState(false);
	const [tanCode, setTanCode] = React.useState('');
	const [challenge, setChallenge] = React.useState(null);
	const [busy, setBusy] = React.useState(false);
	const [error, setError] = React.useState('');
	const [hint, setHint] = React.useState('');
	// true, solange wir den serverseitig gemerkten PIN verwenden (der Request
	// geht dann mit leerem pin-Feld raus, der Server nimmt den Cookie).
	const [usingStored, setUsingStored] = React.useState(!!props.pinUse);

	// Ist ein PIN gemerkt, direkt versuchen statt erst zu fragen (siehe
	// Kommentar oben - bewusst nicht an props.pinStored gebunden).
	//
	// autoTriedRef verhindert einen ECHTEN Doppelaufruf dieses Requests (Chat
	// vom 2026-07-26, Nutzer-Bugreport "zwei PushTAN-Anfragen"): React.StrictMode
	// fuehrt useEffect beim Mounten im Development-Modus absichtlich zweimal aus
	// (mount->cleanup->mount), um Seiteneffekt-Bugs aufzudecken - genau das war
	// hier der Fall, da dieser Request einen echten, nicht wiederholbaren
	// Seiteneffekt bei comdirect ausloest (eine neue PushTAN-Anfrage). Der Ref
	// ueberlebt den StrictMode-Doppeldurchlauf (nur die Effekte laufen zweimal,
	// nicht die Komponente selbst), daher zuverlaessig als Einmal-Schutz nutzbar.
	const autoTriedRef = React.useRef(false);
	React.useEffect(() => {
		if (autoTriedRef.current) return;
		autoTriedRef.current = true;
		if (props.pinUse) {
			setBusy(true);
			task('BrokerageLogin', { connectionId: props.connectionId, pin: '' }, handleLoginResult);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	function submitPin() {
		if (pin === '') return;
		setBusy(true);
		setError('');
		task('BrokerageLogin', { connectionId: props.connectionId, pin, rememberPin: remember ? 1 : 0 }, handleLoginResult);
	}

	function handleLoginResult(result) {
		setBusy(false);
		const content = result.data.content || {};
		if (result.data.returncode !== 1) {
			// Der gemerkte PIN wurde abgelehnt und serverseitig verworfen -
			// zurueckfallen auf manuelle Eingabe statt einer Sackgasse.
			if (content.storedPinCleared) {
				setUsingStored(false);
				setHint('Der gemerkte PIN wurde abgelehnt und verworfen – bitte neu eingeben.');
				setError(content.message || '');
				return;
			}
			if (content.status === 'pin-required') {
				setUsingStored(false);
				return;
			}
			setError(content.message || 'Login fehlgeschlagen.');
			return;
		}
		setChallenge({ type: content.challengeType, text: content.challengeText });
		setTanCode('');
	}

	function submitTan() {
		setBusy(true);
		setError('');
		task('BrokerageTanConfirm', { connectionId: props.connectionId, tanCode, purpose: props.purpose || 'sync' }, (result) => {
			setBusy(false);
			if (result.data.returncode !== 1) {
				setError(result.data.content.message || 'TAN-Bestätigung fehlgeschlagen.');
				return;
			}
			applyBrokerageSession(x, result.data.content);
			setPin('');
			setTanCode('');
			props.onLoggedIn();
		});
	}

	const needsCode = challenge && challenge.type === 'M_TAN';

	return (
		<div className="brokerage-card">
			{props.onCancel && <i className="bi bi-x-lg brokerage-card-close" title="Schließen" onClick={handleCancel}></i>}
			{challenge === null && usingStored
				?
					<p className="brokerage-hint"><i className="bi bi-key"></i> Verwende gemerkte PIN…</p>
				: challenge === null
					?
						<React.Fragment>
							<label htmlFor="brokerage-login-pin">PIN für {props.accountLabel}</label>
							<input
								id="brokerage-login-pin"
								className="brokerage-pin-input"
								type="text"
								inputMode="numeric"
								autoComplete="one-time-code"
								value={pin}
								onChange={(e) => setPin(e.target.value)}
								onKeyDown={(e) => { if (e.key === 'Enter') submitPin(); }}
								autoFocus
							/>
							{props.pinUse &&
								<label className="brokerage-remember">
									<input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} />
									PIN merken (verschlüsselt, nur dieser Browser)
								</label>
							}
							{hint !== '' && <p className="brokerage-hint">{hint}</p>}
							{error !== '' && <p className="brokerage-error"><i className="bi bi-exclamation-triangle"></i> {error}</p>}
							<div className="brokerage-actions">
								<button type="button" className="btn" disabled={busy || pin === ''} onClick={submitPin}>
									{busy ? 'Verbinde…' : 'Anmelden'}
								</button>
							</div>
						</React.Fragment>
					:
						<React.Fragment>
							{challenge.type === 'P_TAN_PUSH' && !busy &&
								<p className="brokerage-hint"><i className="bi bi-phone"></i> Bitte in der comdirect-App bestätigen.</p>
							}
							{challenge.text && !busy && <p className="brokerage-hint">{challenge.text}</p>}
							{needsCode &&
								<React.Fragment>
									<label htmlFor="brokerage-login-tan">TAN</label>
									<input
										id="brokerage-login-tan"
										type="text"
										autoComplete="off"
										value={tanCode}
										onChange={(e) => setTanCode(e.target.value)}
										onKeyDown={(e) => { if (e.key === 'Enter') submitTan(); }}
										autoFocus
									/>
								</React.Fragment>
							}
							{busy && (props.purpose || 'sync') === 'sync' &&
								<p className="brokerage-hint"><i className="bi bi-arrow-repeat"></i> Depot, Orderbuch und Watchlist werden synchronisiert – das kann einen Moment dauern…</p>
							}
							{error !== '' && <p className="brokerage-error"><i className="bi bi-exclamation-triangle"></i> {error}</p>}
							<div className="brokerage-actions">
								{/* Bei PushTAN meldet der Klick, dass die Freigabe in der
								    comdirect-App BEREITS erfolgt ist - deshalb "Bestätigt"
								    (Vergangenheit) statt "Bestätigen" (Nutzervorgabe, Chat
								    vom 2026-08-11). Bei mTAN wird dagegen hier und jetzt
								    mit dem eingegebenen Code bestaetigt, da bleibt es
								    beim Imperativ. */}
								<button type="button" className="btn" disabled={busy || (needsCode && tanCode === '')} onClick={submitTan}>
									{busy ? 'Bestätige…' : (needsCode ? 'Bestätigen' : 'Bestätigt')}
								</button>
							</div>
						</React.Fragment>
			}
		</div>
	)

};
